<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { ExternalLink, Menu, X } from 'lucide-vue-next'
import { RouterLink } from 'vue-router'

const showMobileMenu = ref(false)
const mobileMenuRef = ref<HTMLDivElement | null>(null)

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  if (mobileMenuRef.value && !mobileMenuRef.value.contains(event.target as Node)) {
    showMobileMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <!-- Navigation Bar - Sticky -->
  <nav class="bg-white border-b border-gray-200 sticky top-0 z-50">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 relative">
      <div class="flex items-center justify-between">
        <!-- Logo and Brand -->
        <RouterLink to="/welcome" class="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <img src="/logo.svg" alt="AutomaDoc Logo" class="h-8 w-8 sm:h-10 sm:w-10" />
          <span class="text-lg sm:text-xl font-bold text-gray-800">AutomaDoc</span>
        </RouterLink>

        <!-- Desktop Navigation Links - Hidden on mobile -->
        <div class="hidden md:flex items-center gap-6">
          <RouterLink to="/welcome" class="text-gray-600 hover:text-gray-800 font-medium transition-colors">Home</RouterLink>
          <RouterLink to="/pricing" class="text-gray-600 hover:text-gray-800 font-medium transition-colors">Pricing</RouterLink>
          <RouterLink to="/changelog" class="text-gray-600 hover:text-gray-800 font-medium transition-colors">Changelog</RouterLink>
          <a
            href="https://ksi-digital.jp.larksuite.com/wiki/JRa7wPLwjiZFLWk1hFijJRQppxg"
            target="_blank"
            rel="noopener noreferrer"
            class="text-gray-600 hover:text-gray-800 font-medium transition-colors inline-flex items-center gap-1"
          >
            Docs
            <ExternalLink :size="14" />
          </a>
          <a
            href="mailto:christophe@ksi-digital.com?subject=AutomaDoc%20-%20Inquiry"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Contact Sales
          </a>
        </div>

        <!-- Mobile Menu Button - Shown on mobile only -->
        <div ref="mobileMenuRef" class="md:hidden relative">
          <button
            @click.stop="showMobileMenu = !showMobileMenu"
            class="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            :aria-label="showMobileMenu ? 'Close menu' : 'Open menu'"
          >
            <Menu v-if="!showMobileMenu" :size="24" />
            <X v-else :size="24" />
          </button>

          <!-- Mobile Dropdown Menu -->
          <transition name="dropdown">
            <div
              v-if="showMobileMenu"
              class="absolute top-full right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl w-56"
              style="z-index: 9999;"
            >
              <div class="p-2">
                <a
                  href="/welcome"
                  @click="showMobileMenu = false"
                  class="block px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-md font-medium transition-colors"
                >
                  Home
                </a>
                <RouterLink
                  to="/pricing"
                  @click="showMobileMenu = false"
                  class="block px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-md font-medium transition-colors"
                >
                  Pricing
                </RouterLink>
                <RouterLink
                  to="/changelog"
                  @click="showMobileMenu = false"
                  class="block px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-md font-medium transition-colors"
                >
                  Changelog
                </RouterLink>
                <a
                  href="https://ksi-digital.jp.larksuite.com/wiki/JRa7wPLwjiZFLWk1hFijJRQppxg"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="showMobileMenu = false"
                  class="block px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-md font-medium transition-colors"
                >
                  <span class="inline-flex items-center gap-2">
                    Docs
                    <ExternalLink :size="14" />
                  </span>
                </a>
                <div class="border-t border-gray-200 my-2"></div>
                <a
                  href="mailto:christophe@ksi-digital.com?subject=AutomaDoc%20-%20Inquiry"
                  @click="showMobileMenu = false"
                  class="block px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white text-center rounded-md font-medium transition-colors"
                >
                  Contact Sales
                </a>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
